import { Label } from '@/components/ui/label'
import {
  SidebarMenu as ShadSidebarMenu,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
} from '@/components/ui/sidebar'
import { Switch } from '@/components/ui/switch'
import { appLink } from '@app/config/appLink'
import { getVisibleMenuItems } from '@app/layout/Sidebar/getVisibleMenuItems'
import { SidebarSkeleton } from '@app/layout/Sidebar/Sidebar.skeleton'
import { isAuthenticatedAtom } from '@app/state/authAtom'
import { featureFlagMapAtom } from '@app/state/featureFlagAtoms'
import { sidebarFullyLoadedAtom, sidebarMenuLoadedAtom } from '@app/state/sidebarAtom'
import { ThemeContext } from '@app/ThemeProvider'
import { isItemActive } from '@app/utils/isItemActive'
import { useAtomValue, useSetAtom } from 'jotai'
import { useContext, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import SidebarMenuItemRow from './SidebarMenuItemRow'

export interface ChildMenuItem {
  path: string
  label: string
}

export interface MenuItem {
  icon: string
  label: string
  path: string
  absolutePath?: boolean
  isBeta?: boolean
  children?: ChildMenuItem[]
}

export const SidebarMenu = () => {
  const location = useLocation()
  const isAuthenticated = useAtomValue(isAuthenticatedAtom)
  const featureFlagMap = useAtomValue(featureFlagMapAtom)
  const sidebarFullyLoaded = useAtomValue(sidebarFullyLoadedAtom)
  const setSidebarMenuLoaded = useSetAtom(sidebarMenuLoadedAtom)
  const { isDark, setIsDark } = useContext(ThemeContext)

  const visibleMenuItems = useMemo(() => {
    const items = getVisibleMenuItems(appLink, isAuthenticated, featureFlagMap)
    setSidebarMenuLoaded(true)
    return items
  }, [isAuthenticated, featureFlagMap, setSidebarMenuLoaded])

  const [openSubMenus, setOpenSubMenus] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      visibleMenuItems
        .filter((item) => item.children?.length && isItemActive(item.path, location.pathname))
        .map((item) => [item.icon, true]),
    ),
  )

  const toggleSubMenu = (key: string) => {
    setOpenSubMenus((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  return (
    <SidebarContent>
      <SidebarGroup>
        <SidebarGroupContent className="mt-6">
          <SidebarSkeleton loading={!sidebarFullyLoaded} rows={6} rowHeight={49} />

          {sidebarFullyLoaded && (
            <>
              <ShadSidebarMenu>
                {visibleMenuItems.map((item) => (
                  <SidebarMenuItemRow
                    key={item.icon}
                    item={item}
                    active={isItemActive(item.path, location.pathname)}
                    hasChildren={Boolean(item.children?.length)}
                    isSubOpen={openSubMenus[item.icon] ?? false}
                    location={location}
                    toggleSubMenu={toggleSubMenu}
                  />
                ))}
              </ShadSidebarMenu>
            </>
          )}
        </SidebarGroupContent>
      </SidebarGroup>
    </SidebarContent>
  )
}
